import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  console.log('🔐 AuthProvider rendu');
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    console.log('🔐 useEffect checkAuth déclenché');
    // Timeout de sécurité pour ne pas bloquer le rendu
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('🔐 Timeout checkAuth, arrêt du loading');
        setLoading(false);
      }
    }, 3000); // 3 secondes max
    
    checkAuth().finally(() => {
      clearTimeout(timeout);
      setLoading(false);
    });
  }, []);

  const checkAuth = async () => {
    console.log('🔐 checkAuth appelé');
    try {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (!token || !storedUser) {
        console.log('🔐 Pas de token ou utilisateur stocké');
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('🔐 Token trouvé, vérification avec le backend...');
      // Vérifier que le token est valide avec le backend
      try {
        const response = await api.get('/auth/verify');
        console.log('🔐 Réponse vérification:', response.data);
        if (response.data && response.data.valid) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
          setLoading(false);
        } else {
          // Token invalide - déconnexion silencieuse
          console.log('🔐 Token invalide');
          logout();
        }
      } catch (error) {
        console.warn('🔐 Erreur vérification token:', error.message);
        // Erreur réseau ou serveur indisponible - ne pas déconnecter si c'est juste une erreur réseau
        // Garder la session locale si le serveur est temporairement indisponible
        if (error.response && error.response.status === 401) {
          // Token vraiment invalide - déconnexion
          console.log('🔐 Token invalide (401)');
          logout();
        } else {
          // Erreur réseau - garder la session locale
          console.warn('🔐 Serveur temporairement indisponible, session locale conservée');
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } catch (parseError) {
            logout();
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data && response.data.accessToken) {
        localStorage.setItem('authToken', response.data.accessToken);
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, error: 'Réponse invalide du serveur' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Erreur de connexion. Veuillez vérifier vos identifiants.';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    user,
    setUser,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
